/**
 * rxNumbers that are NOT active on this order and must be hidden:
 *   - drugs split off to troubleshooting (`metadata.troubleshootingSplitHistory`)
 *   - drugs removed from the order (`metadata.itemRemovalHistory`)
 *   - zero-fill drugs deferred to a BO- clone (`metadata.partialFillInfo.filledQuantities[].quantityPackaged === 0`)
 *   - billingData medications carrying `status: 'removed'` (Rx-bump leaves the
 *     stale entry in-array rather than in itemRemovalHistory)
 * No-regression: with none of these signals present the Set is empty.
 */
export const getInactiveRxNumbers = (ord: any): Set<string> => {
  const inactive = new Set<string>();
  const meta = ord?.metadata;
  (meta?.troubleshootingSplitHistory || []).forEach((h: any) => {
    if (h?.rxNumber != null) inactive.add(String(h.rxNumber));
  });
  (meta?.itemRemovalHistory || []).forEach((h: any) => {
    if (h?.rxNumber != null) inactive.add(String(h.rxNumber));
  });
  (meta?.partialFillInfo?.filledQuantities || [])
    .filter((f: any) => Number(f.quantityPackaged) === 0)
    .forEach((f: any) => {
      if (f?.rxNumber != null) inactive.add(String(f.rxNumber));
    });
  (meta?.billingData?.medications || []).forEach((m: any) => {
    if (m?.status === 'removed' && m?.rxNumber != null) inactive.add(String(m.rxNumber));
  });
  return inactive;
};

/**
 * The drugs actually active on an order. Prefers the per-item / billingData.medications
 * detail list over the order-level scalar, minus inactive (split / removed / zero-fill) drugs.
 */
export const getActiveOrderDrugs = (
  ord: any,
): { rxNumbers: string[]; medications?: any[]; removedAny: boolean } => {
  const inactive = getInactiveRxNumbers(ord);
  const isActive = (rx: any): boolean => rx == null || !inactive.has(String(rx));

  const items = Array.isArray(ord?.items) ? ord.items : null;
  const billingMeds = Array.isArray(ord?.metadata?.billingData?.medications)
    ? ord.metadata.billingData.medications
    : null;

  // GP-1131: prefer the per-item detail, and switch to billingData.medications ONLY
  // when the items show the exact per-fill order-spawn corruption signature:
  // several items whose rx_number all collapsed to ONE repeated value while
  // billingData.medications still holds >1 true distinct per-fill Rx (observed on
  // ORD-20260609-181627-653: 8 items all carrying Rx 2383879). Using items alone
  // there collapses the Rx column to a single Rx#.
  //
  // The switch is deliberately NOT "whichever source has more distinct Rx": a
  // cold-chain split (GP-75) moves fridge drugs to a CC- child's order_items but
  // leaves the parent's billingData.medications snapshot intact — the snapshot
  // legitimately has MORE distinct Rx than the parent's real items, and preferring
  // it would resurface the split-off drugs on the parent row (confirmed on prod:
  // ORD-20260604-165617-779, ORD-20260622-211549-811). Any order whose items carry
  // ≥2 distinct Rx is healthy; only the degenerate all-one-Rx shape switches.
  const distinctRxCount = (arr: any[] | null): number =>
    arr ? new Set(arr.map((d) => d?.rxNumber).filter((rx) => rx != null).map(String)).size : 0;
  let detail: any[] | null = (items && items.length > 0 ? items : billingMeds) || null;
  if (
    items &&
    items.length > 1 &&
    distinctRxCount(items) === 1 &&
    billingMeds &&
    distinctRxCount(billingMeds) > 1
  ) {
    detail = billingMeds;
  }

  if (detail) {
    const medications = detail.filter((d: any) => isActive(d?.rxNumber));
    const rxNumbers: string[] = [];
    medications.forEach((d: any) => {
      if (d?.rxNumber && !rxNumbers.includes(String(d.rxNumber))) rxNumbers.push(String(d.rxNumber));
    });
    return { rxNumbers, medications, removedAny: medications.length < detail.length };
  }

  const allRx = String(ord?.rxNumber || '')
    .split(',')
    .map((rx) => rx.trim())
    .filter(Boolean);
  const rxNumbers = allRx.filter((rx) => isActive(rx));
  return { rxNumbers, removedAny: rxNumbers.length < allRx.length };
};
